import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { chatApi } from '../services/chatapi';
import {
  saveChatMessage,
  getChatHistoryPage,
  clearChatHistory as clearStorageHistory
} from '../utils/localStorage';
import useRequirementsSync from './useRequirementsSync';

/**
 * Custom hook for managing chat interactions with the AI agent.
 * Supports regular and streaming messages, and maintains chat state.
 *
 * @param {function|null} onResponse - Optional callback invoked with the AI response message object
 *   after each successful message (regular or streaming). Signature: (aiMessage: { id, role, content, timestamp }) => void.
 *   If not provided, no callback is called.
 */
const useChatAgent = (onResponse = null) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [showThinking, setShowThinking] = useState(true);
  // Persist the session id so chat history survives a reload (otherwise a fresh
  // uuid every mount means getChatHistoryPage() can never find prior messages).
  const [sessionId, setSessionId] = useState(() => {
    try {
      const existing = window.localStorage.getItem('rmdv_active_session');
      if (existing) return existing;
      const fresh = uuidv4();
      window.localStorage.setItem('rmdv_active_session', fresh);
      return fresh;
    } catch {
      return uuidv4();
    }
  });
  const [currentIteration, setCurrentIteration] = useState(0);
  const [maxIterations, setMaxIterations] = useState(10);
  const currentStreamingIdRef = useRef(null);

  // `onResponse` is a caller-supplied callback and is NOT stable: the only
  // production caller passes an inline arrow (RequirementsTreeView renders
  // `<ChatInterface onResponse={() => refreshTree()} />`), so it is a brand-new
  // function on every parent render. Listing it directly in the send callbacks'
  // dependency arrays would therefore re-create sendMessage/sendStreamingMessage
  // on every keystroke in the tree view, churning the hook's public API identity
  // for any consumer that (now or later) puts it in a dependency array — the
  // classic way to restart an SSE stream by accident. This is the "latest value"
  // case: keep it in a ref refreshed each render and read `.current` at call
  // time, which invokes exactly the callback the parent last supplied.
  const onResponseRef = useRef(onResponse);
  useEffect(() => {
    onResponseRef.current = onResponse;
  }, [onResponse]);

  // Local storage pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Tree sync for requirement modifications
  const { notifyRequirementModified } = useRequirementsSync();

  /**
   * Detect requirement modifications from tool calls and notify tree sync
   */
  const detectAndNotifyRequirementModifications = useCallback((thinkingStep) => {
    if (!thinkingStep.content) return;

    const content = thinkingStep.content.toLowerCase();

    // Check for common requirement modification patterns
    const modificationPatterns = [
      /created requirement/i,
      /updated requirement/i,
      /modified requirement/i,
      /deleted requirement/i,
      /added requirement/i,
      /removed requirement/i,
      /requirement created/i,
      /requirement updated/i,
      /requirement modified/i,
      /requirement deleted/i,
      /requirement added/i,
      /requirement removed/i
    ];

    const isModification = modificationPatterns.some(pattern => pattern.test(content));

    if (isModification) {
      // Try to extract requirement ID from the content
      const reqIdMatch = thinkingStep.content.match(/\b([A-Z]+-)+\d+\b/g);
      if (reqIdMatch && reqIdMatch.length > 0) {
        reqIdMatch.forEach(reqId => {
          notifyRequirementModified(reqId, 'modified');
        });
      } else {
        // If no specific ID found, trigger general refresh
        notifyRequirementModified(null, 'modified');
      }
    }
  }, [notifyRequirementModified]);

  // Seed messages from persisted history on mount (and whenever the session
  // changes). Without this the localStorage persistence was write-only —
  // messages were saved but never read back. Page 1 = most recent page.
  useEffect(() => {
    try {
      const firstPage = getChatHistoryPage(sessionId, 1);
      if (firstPage && firstPage.messages.length > 0) {
        setMessages(firstPage.messages);
        setCurrentPage(1);
        setHasMoreHistory(firstPage.hasMore);
      }
    } catch (e) {
      console.warn('Failed to load persisted chat history:', e);
    }
  }, [sessionId]);

  const updateStreamingMessage = useCallback((content) => {
    setStreamingMessage(prev => prev ? { ...prev, content } : null);
  }, []);

  /**
   * Clear all error states
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Toggle thinking steps visibility
   */
  const toggleThinking = useCallback(() => {
    setShowThinking(prev => !prev);
  }, []);

  /**
   * Load more chat history for scroll-to-reload functionality
   */
  const loadMoreHistory = useCallback(() => {
    if (isLoadingHistory || !hasMoreHistory) return;

    setIsLoadingHistory(true);
    try {
      const nextPage = currentPage + 1;
      const historyPage = getChatHistoryPage(sessionId, nextPage);

      if (historyPage.messages.length > 0) {
        // Prepend older messages to the current messages
        setMessages(prev => [...historyPage.messages, ...prev]);
        setCurrentPage(nextPage);
        setHasMoreHistory(historyPage.hasMore);
      } else {
        setHasMoreHistory(false);
      }
    } catch (err) {
      console.warn('Failed to load more history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentPage, hasMoreHistory, isLoadingHistory, sessionId]);

  /**
   * Save message to localStorage
   */
  const saveMessageToStorage = useCallback((message) => {
    saveChatMessage(message, sessionId);
  }, [sessionId]);

  /**
   * Send a message to the chat API (non-streaming)
   */
  const sendMessage = useCallback(async (message, context = null) => {
    if (!message.trim()) return;

    try {
      setLoading(true);
      clearError();

      // Add user message to the chat
      const userMessage = {
        id: uuidv4(),
        role: 'user',
        content: message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);

      // Send to API
      const response = await chatApi.sendMessage(message, context, [], sessionId);

      // Add AI response to the chat
      const aiMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

      // Save messages to localStorage
      saveMessageToStorage(userMessage);
      saveMessageToStorage(aiMessage);

      // Call onResponse callback if provided (latest value via ref — see above)
      if (onResponseRef.current) {
        onResponseRef.current(aiMessage);
      }

      return aiMessage;
    } catch (err) {
      console.error('Error in sendMessage:', err);

      if (err.processedError) {
        setError(err.processedError);
      } else {
        setError({
          message: 'Failed to send message',
          details: err.message || 'Unknown error',
          statusCode: err.status || 500,
          fullError: err
        });
      }

      return null;
    } finally {
      setLoading(false);
    }
    // `sessionId` and `saveMessageToStorage` (itself useCallback([sessionId]))
    // are real dependencies: both were previously captured once, at mount, so
    // after startNewSession() this callback kept posting the OLD session id and
    // persisting into the OLD session's history. sessionId is state that only
    // changes on an explicit new-session, so listing it re-creates the callback
    // at most once per session rotation — no per-keystroke churn. `onResponse`
    // is read through onResponseRef instead (see the ref's comment).
  }, [clearError, saveMessageToStorage, sessionId]);

  /**
   * Send a message to the chat API with streaming response
   */
  const sendStreamingMessage = useCallback(async (message, context = null) => {
    if (!message.trim()) return;

    try {
      clearError();

      // Clear thinking steps and streaming message at the start of a new message
      setThinkingSteps([]);
      setCurrentIteration(0);

      // Capture current messages for history before adding the new user message
      const currentMessages = messages;

      // Add user message to the chat
      const userMessage = {
        id: uuidv4(),
        role: 'user',
        content: message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);

      // Save user message to localStorage
      saveMessageToStorage(userMessage);

      // Create placeholder for streaming response
      const streamMessageId = uuidv4();
      const initialStreamingMessage = {
        id: streamMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        timestamp: new Date()
      };

      setStreamingMessage(initialStreamingMessage);
      currentStreamingIdRef.current = streamMessageId;

      setLoading(true); // Disable send button during streaming

      // Start streaming
      const response = await chatApi.streamMessage(
        message,
        context,
        currentMessages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(msg => ({
            role: msg.role,
            content: msg.content
          })),
        (chunk, accumulated) => {
          // Handle different chunk types
          if (chunk.type === 'heartbeat') {
            // This is a heartbeat message, log it for debugging
            console.log('Received heartbeat:', chunk.content || chunk.chunk);
            // Don't update the UI content for heartbeats
          } else if (chunk.type === 'thinking') {
            // This is a thinking step, add it to thinking steps
            console.log('Processing thinking chunk:', chunk);
            const thinkingStep = {
              id: uuidv4(),
              content: chunk.content || chunk.chunk,
              type: chunk.thinking_type || 'general',
              timestamp: new Date()
            };

            console.log('Adding thinking step:', thinkingStep);
            setThinkingSteps(prev => [...prev, thinkingStep]);

            // Check if this is a tool call that modifies requirements
            if (thinkingStep.type === 'tool_use') {
              detectAndNotifyRequirementModifications(thinkingStep);
            }

            // If it's an iteration update, update current iteration counter
            if (chunk.thinking_type === 'iteration') {
              setCurrentIteration(chunk.iteration || 0);
              setMaxIterations(chunk.max_iterations || 10);
            }
          } else {
            // This is regular content, update the streaming message
            // Use the accumulated content from the API
            const contentToUse = accumulated || chunk.content || chunk.chunk || '';

            if (contentToUse) {
              console.log('Updating streaming message with content:', contentToUse.substring(0, 50) + '...');
              updateStreamingMessage(contentToUse);
            }
          }
        },
        sessionId
      );

      // When streaming is complete, add the message to history and clear streaming state
      const finalContent = response.message;
      console.log('Streaming complete, finalContent:', finalContent);
      const finalAiMessage = {
        id: uuidv4(), // Generate new unique ID for final message
        role: 'assistant',
        content: finalContent || "I apologize, but I was unable to generate a response. Please try rephrasing your question or providing more context.",
        timestamp: new Date()
      };

      console.log('Adding final AI message to messages:', { id: finalAiMessage.id, content: finalAiMessage.content.substring(0, 50) + '...' });
      setMessages(prev => [...prev, finalAiMessage]);
      setStreamingMessage(null);
      currentStreamingIdRef.current = null;

      // Save AI message to localStorage
      saveMessageToStorage(finalAiMessage);

      // Call onResponse callback if provided (latest value via ref — see above)
      if (onResponseRef.current) {
        onResponseRef.current(finalAiMessage);
      }

      setLoading(false); // Re-enable send button after streaming completes

      return finalAiMessage;
    } catch (err) {
      console.error('Error in sendStreamingMessage:', err);

      // If we have a streaming message in progress, add it as is
      if (streamingMessage && streamingMessage.content) {
        const partialMessage = {
          ...streamingMessage,
          isStreaming: false,
          content: streamingMessage.content + '\n\n_Message was interrupted due to an error._'
        };

        setMessages(prev => [...prev, partialMessage]);
      }

      // Clear streaming state
      setStreamingMessage(null);
      currentStreamingIdRef.current = null;

      if (err.processedError) {
        setError(err.processedError);
      } else {
        setError({
          message: 'Failed to receive streaming response',
          details: err.message || 'Unknown error',
          statusCode: err.status || 500,
          fullError: err
        });
      }

      return null;
    } finally {
      setLoading(false);
      // Don't clear streamingMessage here - the success path handles it
    }
    // `detectAndNotifyRequirementModifications` (useCallback -> notifyRequirementModified
    // -> triggerRefresh, which is useCallback([])) and `updateStreamingMessage`
    // (useCallback([])) are both stable for the life of the provider, so listing
    // them adds no re-creation at all. `sessionId`/`saveMessageToStorage` change
    // only on a session rotation — and were previously stale-captured at mount,
    // so a new session still streamed under the old id. This callback's identity
    // is never consumed by an effect or a dependency array (it is called from the
    // submit handler), and it already changed on every `messages` update, so a
    // wider array cannot restart an in-flight stream: the running request is held
    // by the executing async function, not by the callback reference. `onResponse`
    // stays out of the array deliberately and is read via onResponseRef.
  }, [
    messages,
    streamingMessage,
    clearError,
    detectAndNotifyRequirementModifications,
    saveMessageToStorage,
    sessionId,
    updateStreamingMessage,
  ]);

  /**
   * Clear chat history (both locally and on the server)
   */
  const clearHistory = useCallback(async () => {
    try {
      setLoading(true);

      // Clear messages on server (chatApi exposes a clearHistory() method; the
      // axios client is a separate default export, not a property of chatApi).
      await chatApi.clearHistory();

      // Clear persisted history for this session (was previously left behind,
      // so stale messages could resurface).
      clearStorageHistory(sessionId);

      // Clear local state
      setMessages([]);
      setStreamingMessage(null);
      setThinkingSteps([]);
      currentStreamingIdRef.current = null;
      setCurrentPage(1);
      setHasMoreHistory(false);
      clearError();

      return true;
    } catch (err) {
      console.error('Error clearing history:', err);

      if (err.processedError) {
        setError(err.processedError);
      } else {
        setError({
          message: 'Failed to clear history',
          details: err.message || 'Unknown error',
          statusCode: err.status || 500
        });
      }

      return false;
    } finally {
      setLoading(false);
    }
  }, [clearError, sessionId]);

  /**
   * Start a new chat session
   */
  const startNewSession = useCallback(() => {
    // Generate new session ID and persist it as the active session.
    const newSessionId = `session-${Date.now()}`;
    setSessionId(newSessionId);
    try {
      window.localStorage.setItem('rmdv_active_session', newSessionId);
    } catch { /* ignore storage errors */ }

    // Clear messages and errors
    setMessages([]);
    setStreamingMessage(null);
    setThinkingSteps([]);
    currentStreamingIdRef.current = null;
    clearError();

    // Reset pagination
    setCurrentPage(1);
    setHasMoreHistory(false);

    return newSessionId;
  }, [clearError]);

  return {
    messages,
    loading,
    error,
    streamingMessage,
    thinkingSteps,
    showThinking,
    toggleThinking,
    currentIteration,
    maxIterations,
    sendMessage,
    sendStreamingMessage,
    clearHistory,
    clearError,
    sessionId,
    startNewSession,
    loadMoreHistory,
    hasMoreHistory,
    isLoadingHistory
  };
};

export default useChatAgent;